import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@bangle.io/base-ui';
import React from 'react';

export type WorkspaceStatsBreakdownRow = {
  id: string;
  label: string;
  sublabel?: string;
  value: string;
  accent?: boolean;
};

export type WorkspaceStatsStripLabels = {
  words: string;
  pages: string;
  read: string;
  wordsDetails: string;
  pagesDetails: string;
  readDetails: string;
  characters: string;
  charactersNoSpaces: string;
  charactersNoSpacesSublabel: string;
  avgWordsPerDoc: string;
  documents: string;
  wordsPerPage: string;
  wordsPerPageHint: string;
  goals: string;
  goalsDetails: string;
  goalWordTarget: string;
  goalWordTargetHint: string;
  goalDeadline: string;
  goalDeadlineHint: string;
  wordsPerDay: string;
  wordsPerDayHint: string;
  clearGoal: string;
  goalProgress: string;
  daysRemaining: ({ count }: { count: number }) => string;
  dailyProgress: string;
};

export type WorkspaceStatsGoals = {
  goalWordCount: number | null;
  goalDeadline: string | null;
  wordsPerDayGoal: number | null;
  wordsWrittenToday: number;
  daysRemaining: number | null;
};

export type WorkspaceStatsStripProps = {
  words: number;
  chars: number;
  charsNoSpaces: number;
  docs: number;
  pagesValue: string;
  readValue: string;
  wordsPerPage: number;
  readingRows: readonly WorkspaceStatsBreakdownRow[];
  labels: WorkspaceStatsStripLabels;
  onWordsPerPageChange: (wordsPerPage: number) => void;
  goals?: WorkspaceStatsGoals;
  onGoalWordCountChange?: (goalWordCount: number | null) => void;
  onGoalDeadlineChange?: (goalDeadline: string | null) => void;
  onWordsPerDayGoalChange?: (wordsPerDayGoal: number | null) => void;
  className?: string;
};

function StatsCell({
  label,
  value,
  title,
  dialogLabel,
  rows,
  footer,
}: {
  label: string;
  value: string;
  title: string;
  dialogLabel: string;
  rows: readonly WorkspaceStatsBreakdownRow[];
  footer?: React.ReactNode;
}) {
  return (
    <DropdownMenu
      // Footer inputs need stable focus; hover-highlight would steal it.
      highlightItemOnHover={!footer}
    >
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            title={title}
            aria-label={dialogLabel}
            className="h-auto w-full min-w-0 flex-col gap-1 rounded-md px-1 py-1.5 text-center hover:bg-sidebar-accent"
          >
            <span className="w-full truncate font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
              {label}
            </span>
            <span className="w-full truncate font-semibold text-base text-sidebar-foreground tabular-nums leading-none">
              {value}
            </span>
          </Button>
        }
      />
      <DropdownMenuContent
        align="center"
        side="top"
        sideOffset={6}
        className="w-auto min-w-44"
        aria-label={dialogLabel}
      >
        <DropdownMenuLabel className="sr-only">{dialogLabel}</DropdownMenuLabel>
        <div className="flex flex-col gap-0.5 py-0.5">
          {rows.map((row) => (
            <div
              key={row.id}
              className={cn(
                'flex items-center justify-between gap-3 px-1.5 py-1 text-xs tabular-nums',
                row.accent ? 'text-sidebar-primary' : 'text-popover-foreground',
              )}
            >
              <span
                className={cn(
                  'min-w-0 leading-snug',
                  !row.accent && 'text-muted-foreground',
                )}
              >
                <span className="block">{row.label}</span>
                {row.sublabel ? (
                  <span className="block text-[10px] opacity-90">
                    {row.sublabel}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 leading-none">{row.value}</span>
            </div>
          ))}
          {footer}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function keepMenuOpenOnPointerDown(event: React.PointerEvent) {
  // Keep the dropdown open while interacting with footer controls, but do not
  // cancel the default on inputs — that blocks click-to-focus.
  if (
    event.target instanceof HTMLInputElement ||
    event.target instanceof HTMLTextAreaElement ||
    event.target instanceof HTMLSelectElement
  ) {
    return;
  }
  event.preventDefault();
}

/**
 * Base UI Menu typeahead/list-navigation listen on the popup and swallow
 * printable keys that bubble from nested inputs. Stop those here so Goals /
 * Pages fields stay typeable; let Escape still dismiss the menu.
 */
function keepMenuFromStealingInputKeys(event: React.KeyboardEvent) {
  if (event.key === 'Escape') {
    return;
  }
  event.stopPropagation();
}

function formatGoalNumberDraft(value: number | null | undefined): string {
  return value != null ? String(value) : '';
}

function parseGoalNumberDraft(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const next = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(next) || next <= 0) {
    return null;
  }
  return next;
}

/**
 * Local draft for async-backed goal number fields so multi-digit typing stays
 * visible while workspace metadata catches up.
 */
function GoalNumberInput({
  value,
  onChange,
  label,
  hint,
  placeholder = '—',
}: {
  value: number | null | undefined;
  onChange?: (value: number | null) => void;
  label: string;
  hint: string;
  placeholder?: string;
}) {
  const [focused, setFocused] = React.useState(false);
  const [draft, setDraft] = React.useState(() => formatGoalNumberDraft(value));

  React.useEffect(() => {
    if (!focused) {
      setDraft(formatGoalNumberDraft(value));
    }
  }, [focused, value]);

  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      <input
        // text + inputMode avoids type=number quirks inside menus while still
        // offering a numeric keypad on mobile. Persist on blur only so async
        // workspace metadata updates cannot fight mid-keystroke drafts.
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        className="h-7 rounded-md border border-input bg-background px-2 text-xs tabular-nums outline-none focus-visible:ring-1 focus-visible:ring-ring"
        value={draft}
        placeholder={placeholder}
        aria-label={label}
        title={hint}
        onKeyDown={keepMenuFromStealingInputKeys}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          const parsed = parseGoalNumberDraft(draft);
          if (parsed == null && draft.trim() !== '') {
            setDraft(formatGoalNumberDraft(value));
            return;
          }
          setDraft(formatGoalNumberDraft(parsed));
          if (parsed !== (value ?? null)) {
            onChange?.(parsed);
          }
        }}
        onChange={(event) => {
          setDraft(event.target.value.replace(/[^\d]/g, ''));
        }}
      />
    </label>
  );
}

export function WorkspaceStatsStrip({
  words,
  chars,
  charsNoSpaces,
  docs,
  pagesValue,
  readValue,
  wordsPerPage,
  readingRows,
  labels,
  onWordsPerPageChange,
  goals,
  onGoalWordCountChange,
  onGoalDeadlineChange,
  onWordsPerDayGoalChange,
  className,
}: WorkspaceStatsStripProps) {
  const totalWords = Math.max(0, Number(words) || 0);
  const totalChars = Math.max(0, Number(chars) || 0);
  const totalCharsNoSpaces = Math.max(0, Number(charsNoSpaces) || 0);
  const totalDocs = Math.max(0, Number(docs) || 0);
  const avg = totalDocs === 0 ? 0 : Math.round(totalWords / totalDocs);

  const wordsRows = React.useMemo<WorkspaceStatsBreakdownRow[]>(
    () => [
      {
        id: 'chars',
        label: labels.characters,
        value: totalChars.toLocaleString(),
      },
      {
        id: 'charsNoSpaces',
        label: labels.charactersNoSpaces,
        sublabel: labels.charactersNoSpacesSublabel,
        value: totalCharsNoSpaces.toLocaleString(),
      },
      {
        id: 'avg',
        label: labels.avgWordsPerDoc,
        value: avg.toLocaleString(),
      },
    ],
    [
      avg,
      labels.avgWordsPerDoc,
      labels.characters,
      labels.charactersNoSpaces,
      labels.charactersNoSpacesSublabel,
      totalChars,
      totalCharsNoSpaces,
    ],
  );

  const pagesRows = React.useMemo<WorkspaceStatsBreakdownRow[]>(
    () => [
      {
        id: 'docs',
        label: labels.documents,
        value: totalDocs.toLocaleString(),
      },
    ],
    [labels.documents, totalDocs],
  );

  const goalRows = React.useMemo<WorkspaceStatsBreakdownRow[]>(() => {
    if (!goals) {
      return [];
    }
    const rows: WorkspaceStatsBreakdownRow[] = [];
    if (goals.goalWordCount != null) {
      rows.push({
        id: 'word-goal',
        label: labels.goalProgress,
        value: `${totalWords.toLocaleString()} / ${goals.goalWordCount.toLocaleString()}`,
        accent: totalWords >= goals.goalWordCount,
      });
    }
    if (goals.goalDeadline != null && goals.daysRemaining != null) {
      rows.push({
        id: 'deadline',
        label: labels.goalDeadline,
        value: labels.daysRemaining({ count: goals.daysRemaining }),
      });
    }
    if (goals.wordsPerDayGoal != null) {
      rows.push({
        id: 'daily',
        label: labels.dailyProgress,
        value: `${goals.wordsWrittenToday.toLocaleString()} / ${goals.wordsPerDayGoal.toLocaleString()}`,
        accent: goals.wordsWrittenToday >= goals.wordsPerDayGoal,
      });
    }
    return rows;
  }, [goals, labels, totalWords]);

  const goalSummary =
    goals?.goalWordCount != null
      ? `${Math.min(100, Math.round((totalWords / goals.goalWordCount) * 100))}%`
      : goals?.wordsPerDayGoal != null
        ? `${Math.min(100, Math.round((goals.wordsWrittenToday / goals.wordsPerDayGoal) * 100))}%`
        : '—';

  return (
    <div
      data-testid="workspace-stats-strip"
      className={cn(
        'border-sidebar-border border-t px-2 py-2 text-center',
        className,
      )}
    >
      <div className="grid grid-cols-4 gap-0.5">
        <StatsCell
          label={labels.words}
          value={totalWords.toLocaleString()}
          title={labels.wordsDetails}
          dialogLabel={labels.wordsDetails}
          rows={wordsRows}
        />
        <StatsCell
          label={labels.pages}
          value={pagesValue}
          title={labels.pagesDetails}
          dialogLabel={labels.pagesDetails}
          rows={pagesRows}
          footer={
            <label
              className="mt-1 flex flex-col gap-1 border-border border-t px-1.5 pt-2 pb-1 text-left"
              onPointerDown={keepMenuOpenOnPointerDown}
            >
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                {labels.wordsPerPage}
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                className="h-7 rounded-md border border-input bg-background px-2 text-xs tabular-nums outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={wordsPerPage}
                aria-label={labels.wordsPerPage}
                title={labels.wordsPerPageHint}
                onKeyDown={keepMenuFromStealingInputKeys}
                onChange={(event) => {
                  const next = Number.parseInt(event.target.value, 10);
                  if (Number.isFinite(next) && next > 0) {
                    onWordsPerPageChange(next);
                  }
                }}
              />
              <span className="text-[10px] text-muted-foreground leading-snug">
                {labels.wordsPerPageHint}
              </span>
            </label>
          }
        />
        <StatsCell
          label={labels.read}
          value={readValue}
          title={labels.readDetails}
          dialogLabel={labels.readDetails}
          rows={readingRows}
        />
        <StatsCell
          label={labels.goals}
          value={goalSummary}
          title={labels.goalsDetails}
          dialogLabel={labels.goalsDetails}
          rows={goalRows}
          footer={
            <div
              className="mt-1 flex flex-col gap-2 border-border border-t px-1.5 pt-2 pb-1 text-left"
              onPointerDown={keepMenuOpenOnPointerDown}
            >
              <GoalNumberInput
                value={goals?.goalWordCount}
                onChange={onGoalWordCountChange}
                label={labels.goalWordTarget}
                hint={labels.goalWordTargetHint}
              />
              <label className="flex flex-col gap-1">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
                  {labels.goalDeadline}
                </span>
                <input
                  type="date"
                  className="h-7 rounded-md border border-input bg-background px-2 text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={goals?.goalDeadline ?? ''}
                  aria-label={labels.goalDeadline}
                  title={labels.goalDeadlineHint}
                  onKeyDown={keepMenuFromStealingInputKeys}
                  onChange={(event) => {
                    const next = event.target.value.trim();
                    onGoalDeadlineChange?.(next || null);
                  }}
                />
              </label>
              <div className="flex flex-col gap-1">
                <GoalNumberInput
                  value={goals?.wordsPerDayGoal}
                  onChange={onWordsPerDayGoalChange}
                  label={labels.wordsPerDay}
                  hint={labels.wordsPerDayHint}
                />
                <span className="text-[10px] text-muted-foreground leading-snug">
                  {labels.wordsPerDayHint}
                </span>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}
